/**
 * Strips HTML tags and common HTML entities (like &nbsp;) from a string.
 * @param {string} html - The input HTML string.
 * @returns {string} - Clean text with tags and &nbsp; removed.
 */
export const stripHtml = (html) => {
  if (!html) return '';
  return html
    .replace(/\\u003C/gi, '<')
    .replace(/\\u003E/gi, '>')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replaceAll(/<[^>]*>/g, '') // remove HTML tags
    .replaceAll('&nbsp;', '') // remove &nbsp; with ''
    .replaceAll('&amp;', '&') // replace &amp; with &
    .replaceAll('&quot;', '')
    .trim(); // remove leading/trailing spaces;
};

/**
 * Build serializable rich text props for DSM text objects.
 * This preserves HTML rendering without passing React nodes.
 */
export const richTextHtmlProps = (html) => ({
  dangerouslySetInnerHTML: {
    __html: typeof html === 'string' ? html : html == null ? '' : String(html),
  },
});

/**
 * Safe array helper
 */
export const safeArray = (value) => (Array.isArray(value) ? value : []);

/**
 * Safe string helper
 */
export const safeString = (value) => (typeof value === 'string' ? value : '');

/**
 * Finds and returns the first object in an array that matches the given key.
 *
 * @param {Array<Object>} arr - Array of objects containing key-value pairs
 * @param {string} keyToMatch - The key value to search for
 * @returns {Object|undefined} - The matched object, or undefined if not found
 */
export const getObjectByKey = (arr, keyToMatch) => {
  // Use Array.prototype.find to locate the first matching object
  let obj;
  if (arr && Array.isArray(arr)) {
    obj = arr.find((item) => item.key === keyToMatch);
  }
  return obj;
};

/**
 * Extract GraphQL URL from CTA links
 * @param {Array} ctas - Array of CTA objects
 * @returns {string} - GraphQL endpoint URL or empty string
 */
export const isGraphQLUrl = (value) => {
  if (typeof value !== 'string' || !value.trim()) return false;

  const trimmedValue = value.trim();

  try {
    const path = trimmedValue.startsWith('/') ? trimmedValue : new URL(trimmedValue).pathname;
    return path.startsWith('/graphql/execute.json/');
  } catch {
    return false;
  }
};

export const getGraphQLUrlFromCTAs = (ctas) => {
  if (!Array.isArray(ctas)) return '';
  const graphQlCta = ctas.find((cta) => isGraphQLUrl(cta.link));
  return graphQlCta?.link?.trim() || '';
};

/**
 * Extract number of columns from customFields
 */
export const getNumberOfColumns = (customFields = []) => {
  const field = customFields?.find((f) => f.key?.toLowerCase() === 'number of columns');

  if (!field?.value) return undefined;

  const parsed = Number.parseInt(stripHtml(field.value), 10);
  return Number.isNaN(parsed) ? undefined : parsed;
};

/**
 * Extracts the view count from the provided data array.
 *
 * @param {Array<Object>} data - The array of objects to search for the 'View' key.
 * @returns {number} The extracted view count. Defaults to 30 if not found.
 */
export const extractViewCount = (data) => {
  let viewCount = 30;
  if (data && Array.isArray(data)) {
    const customField = data.find((field) => field.key === 'View');
    viewCount = stripHtml(customField?.value) || 30;
  }
  return Number(viewCount);
};

/**
 * Generates an array of view options based on the given view count and card length.
 *
 * @param {number} viewCount - The increment value for each option.
 * @param {number} cardLength - The maximum value for the options.
 * @returns {Array<{value: number|string, label: string}>} An array of option objects with `value` and `label` properties.
 */
export const createViewOptions = (viewCount, cardLength) => {
  const options = [];

  for (let i = viewCount; i <= cardLength; i += viewCount) {
    options.push({
      value: i,
      label: String(i),
    });
  }

  options.push({
    value: 'all',
    label: 'All',
  });

  return options;
};

export function extractUrl(input) {
  if (!Array.isArray(input) || !input[0] || !input[0].value) {
    return null;
  }

  const html = input[0].value;

  return html ? stripHtml(html) : null;
}

export const toCamelCase = (str = '') => {
  return str
    .replaceAll(/[^a-zA-Z0-9 ]/g, '')
    .trim()
    .split(/\s+/)
    .map((w, i) =>
      i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
    )
    .join('');
};

export const getContentPosition = (settings) => {
  const vertical = settings?.contentAlignment || 'top';
  const horizontal = settings?.textPlacement || 'left';

  return `${vertical}-${horizontal}`;
};

/**
 * Sanitize a string field on each object item in an array.
 * Returns the original item when no change is needed.
 */
export const sanitizeArrayObjectStringField = (items, fieldName) => {
  if (!Array.isArray(items)) return items;

  return items.map((item) => {
    if (!item || typeof item !== 'object') return item;
    if (typeof item[fieldName] !== 'string') return item;

    const sanitizedValue = stripHtml(item[fieldName]);
    if (sanitizedValue === item[fieldName]) return item;

    return {
      ...item,
      [fieldName]: sanitizedValue,
    };
  });
};
